/**
 * Finding the verbal root behind a dictionary headword.
 *
 * A root's spelling in the dictionary is not its *aupadeśika* form, and the
 * difference is not cosmetic: `aki~\` is the root *aṅk*, with the nasal living
 * in an `it` marker rather than in the letters, and conjugating from a bare
 * `aṅk` gives the wrong verb. `data/roots.json` is therefore generated by asking
 * the engine to derive every Dhātupāṭha entry, and indexed by what it actually
 * produced.
 *
 * The index is ~217 KB and is fetched only when a root is looked up.
 */

import { Dhatu, Gana, Pada } from "./verb-types";

export type RootEntry = {
    /** Dhātupāṭha reference, `01.1137` — gaṇa and position. */
    code: string;
    aupadeshika: string;
    gana: Gana;
    /** Surface forms this root is known by, in IAST. */
    forms: string[];
    /** The Dhātupāṭha's own gloss. */
    artha: string;
};

let cache: RootEntry[] | null = null;
let byForm: Map<string, RootEntry[]> | null = null;
let pending: Promise<void> | null = null;

const index = (entries: RootEntry[]) => {
    const m = new Map<string, RootEntry[]>();
    for (const e of entries) {
        for (const f of e.forms) {
            const list = m.get(f);
            if (list) list.push(e); else m.set(f, [e]);
        }
    }
    return m;
};

/** Loads the root index, once. */
export const loadRoots = (): Promise<void> => {
    if (cache) return Promise.resolve();
    if (pending) return pending;

    pending = import("./data/roots.json")
        .then(m => {
            cache = ((m.default ?? m) as unknown) as RootEntry[];
            byForm = index(cache);
        })
        .catch(() => {
            cache = [];
            byForm = new Map();
        });

    return pending;
};

/** Whether the index is resident. */
export const rootsLoaded = (): boolean => cache !== null;

const GANA_BY_NUMBER: Record<string, Gana> = {
    "1": "Bhvadi", "2": "Adadi", "3": "Juhotyadi", "4": "Divadi", "5": "Svadi",
    "6": "Tudadi", "7": "Rudhadi", "8": "Tanadi", "9": "Kryadi", "10": "Curadi",
};

/**
 * Monier-Williams records a root's class and pada in a `cp` attribute, as
 * `1P`, `1P,1Ā`, `10P` and so on. This narrows a headword that matches several
 * Dhātupāṭha entries — *vid* alone sits in five gaṇas, and they are different
 * verbs.
 */
export type VerbClass = { gana: Gana; pada?: Pada };

export const parseVerbClasses = (cp: string): VerbClass[] => {
    const out: VerbClass[] = [];
    const seen = new Set<string>();
    // Ā is written both as the precomposed letter and as A with a macron.
    for (const m of cp.matchAll(/(\d+)\s*([PĀAĀ]?)/g)) {
        const gana = GANA_BY_NUMBER[m[1]];
        if (!gana) continue;
        const pada: Pada | undefined =
            m[2] === "P" ? "Parasmaipada"
            : m[2] && m[2] !== "" ? "Atmanepada"
            : undefined;
        const key = `${gana}|${pada ?? ""}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ gana, pada });
    }
    return out;
};

/**
 * Every root matching a headword. When the dictionary states the class, matching
 * entries are listed first — but the others are kept, because MW and the
 * Dhātupāṭha do not always agree and hiding a real reading is worse than
 * offering a choice.
 */
export const findRoots = (headword: string, cp?: string): RootEntry[] => {
    if (!byForm) return [];
    const matches = byForm.get(headword.trim().toLowerCase()) ?? [];
    if (matches.length <= 1 || !cp) return matches;

    const wanted = new Set(parseVerbClasses(cp).map(c => c.gana));
    if (wanted.size === 0) return matches;

    return [...matches].sort((a, b) => {
        const av = wanted.has(a.gana) ? 0 : 1;
        const bv = wanted.has(b.gana) ? 0 : 1;
        return av - bv;
    });
};

/** Whether a headword is a root we can conjugate. */
export const isRoot = (headword: string): boolean =>
    (byForm?.get(headword.trim().toLowerCase())?.length ?? 0) > 0;

/** The engine argument for a root entry. */
export const toDhatu = (entry: RootEntry): Dhatu => ({
    aupadeshika: entry.aupadeshika,
    gana: entry.gana,
});

/** Human-readable gaṇa label, e.g. "class 1 (bhvādi)". */
export const GANA_LABEL: Record<Gana, string> = {
    Bhvadi: "class 1 (bhvādi)",
    Adadi: "class 2 (adādi)",
    Juhotyadi: "class 3 (juhotyādi)",
    Divadi: "class 4 (divādi)",
    Svadi: "class 5 (svādi)",
    Tudadi: "class 6 (tudādi)",
    Rudhadi: "class 7 (rudhādi)",
    Tanadi: "class 8 (tanādi)",
    Kryadi: "class 9 (kryādi)",
    Curadi: "class 10 (curādi)",
    Kandvadi: "kaṇḍvādi",
};
